/* eslint-disable lingui/no-unlocalized-strings -- Hostnames and page names are wire identifiers. */

/**
 * Which of our sites an event came from. One bundle serves every host, and "/" is a different
 * page on each (the First Round home on firstround.com.au, the hub on theresume.io), so every event
 * carries this. www and the bare domain are one site.
 */
export const resolveSite = (hostname: string) => {
  const host = hostname.toLowerCase().replace(/^www\./, "");
  if (host === "localhost" || host === "127.0.0.1") return "localhost";
  return host;
};

const isFirstRoundSite = (site: string) =>
  site === "firstround.com.au" || site.startsWith("firstround.");

// Routes whose page name isn't simply their path. Keys are AnalyticsTracker's normalised routes.
const NAMED_ROUTES: Record<string, string> = {
  "/first-round": "first_round_home",
  "/demo/practice/:sessionId": "demo_results",
  "/auth/login": "login",
  "/auth/register": "register",
  "/auth/complete-profile": "complete_profile",
  "/auth/account-link-conflict": "account_link_conflict",
  "/dashboard": "dashboard",
  "/dashboard/practice-interview": "practice_home",
  "/dashboard/practice-interview/preview": "practice_prep",
  "/dashboard/practice-interview/session/:sessionId": "practice_session",
  "/dashboard/practice-interview/results/:sessionId": "practice_results",
  "/dashboard/practice-interview/history": "practice_history",
  "/dashboard/practice-interview/demo": "practice_trial",
  "/dashboard/resumes": "resumes",
  "/builder/:id": "builder",
  // Interviews recruiters send to candidates (not Practice Interview).
  "/dashboard/interview": "interviews",
  "/dashboard/interview/:interviewId": "interview_room",
  "/dashboard/interview/verify/:interviewRequestId": "interview_verify",
  "/dashboard/jobs": "jobs",
  "/dashboard/jobs/:jobId": "job_detail",
  "/dashboard/application-status": "application_status",
  "/dashboard/spotlight-intro": "spotlight_intro",
  "/dashboard/spotlight-intro/:id": "spotlight_intro_detail",
  // Public pages. Their paths are only ids, so the fallback would name them all "_username".
  "/:username": "public_profile",
  "/:username/:slug": "public_resume",
  "/u/:username": "public_career_page",
  "/board/:boardSlug": "job_board",
  "/board/:boardSlug/:companySlug": "job_board_company",
  "/board/:boardSlug/job/:jobId": "job_board_job",
  "/board/:boardSlug/:companySlug/:jobSlug": "job_board_job",
  "/open-positions/:jobId": "open_position",
  "/job-seekers": "job_seekers",
  "/practice-interview": "practice_interview_marketing",
  "/pricing": "pricing_redirect",
};

// The First Round legal set, at "/x" on the brand domain and "/first-round/x" elsewhere.
const LEGAL = new Set(["legal", "terms", "privacy", "trial-terms", "trial-privacy"]);

const fromPath = (route: string) =>
  route
    .replace(/^\/+|\/+$/g, "")
    .replace(/\/:[^/]+/g, "")
    .replace(/[^a-z0-9]+/gi, "_")
    .toLowerCase() || "home";

/**
 * A stable, readable name for the page: "first_round_home", "register", "practice_session"…
 * Report breakdowns use it instead of raw routes, which differ between hosts for the same page.
 */
export const pageName = (route: string, site: string) => {
  if (route === "/") return isFirstRoundSite(site) ? "first_round_home" : "hub";

  const named = NAMED_ROUTES[route];
  if (named) return named;

  const bare = route.replace(/^\/first-round(?=\/)/, "");
  const first = bare.split("/")[1] ?? "";
  if (LEGAL.has(first) && (isFirstRoundSite(site) || route.startsWith("/first-round/"))) {
    return `legal_${first.replace(/-/g, "_")}`;
  }
  if (first.startsWith("for-")) return `industry_${first.slice(4).replace(/-/g, "_")}`;

  return fromPath(route);
};
